/**
 * Purpose: Global game state provider — single source of truth for the entire session.
 * Owns: Game state, dispatch actions for all game events, question pack loading.
 * Notes: Redesigned for the "School Day" classroom progression system.
 *        Uses React Context for Phase 1 (simple, sufficient for single-device).
 *        Standard modes: question → answer → if correct, roll dice → advance seats.
 *        Pure Classroom mode: question → answer → next (no dice, no movement).
 *        If state management grows complex, this can be refactored to useReducer or
 *        an external state library without changing the component API.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { GameState, GameMode, Major, ScheduleMode, ClassPeriod } from '../types/game';
import type { QuestionPack, AgeGroup, Tier, Category } from '../types/question';
import {
  createInitialGameState,
  createPlayer,
  resetGameForRematch,
} from '../engine/gameState';
import {
  advanceToNextTurn,
  startGame as startGameEngine,
  processCorrectAnswer,
  processWrongAnswer,
  processPureClassroomAnswer,
  checkWinCondition,
} from '../engine/turnManager';
import { rollDice, type DiceValue } from '../engine/diceRoller';
import { didPlayerTakeLead } from '../engine/scoring';
import {
  getCurrentSubjectForPlayer,
  generateSchedule,
  generatePureClassroomSchedule,
  createEmptyClassProgress,
} from '../engine/seatProgression';
import { selectQuestion } from '../engine/questionSelector';
import { getReaction } from '../ai/hostReactions';
import { saveGame, clearSave } from '../utils/saveGame';
import {
  TOTAL_PERIODS,
  SUBJECT_CLASS_SIZE,
  FINAL_EXAM,
  FINAL_EXAM_LABEL,
  FINAL_EXAM_PERIOD,
  PURE_CLASSROOM_QUESTIONS,
  PURE_CLASSROOM_FINAL_EXAM_QUESTIONS,
} from '../config/classConfig';

// _______________________________________________
//              CONTEXT SHAPE
// _______________________________________________

interface GameContextValue {
  /** Current full game state */
  state: GameState;
  /** The loaded question pack (null if not loaded yet) */
  pack: QuestionPack | null;
  /** Load a question pack JSON */
  loadPack: (pack: QuestionPack) => void;
  /** Add a player during setup */
  addPlayer: (name: string, ageGroup: AgeGroup, tier: Tier, major?: Major) => void;
  /** Remove a player during setup */
  removePlayer: (playerId: string) => void;
  /** Set the win condition mode (Speed Run or Honor Roll) */
  setGameMode: (mode: GameMode) => void;
  /** Set the schedule mode (Random or Choose) */
  setScheduleMode: (mode: ScheduleMode) => void;
  /** Transition from setup to the first turn, optionally with a custom subject order */
  startGame: (customSubjectOrder?: Category[]) => void;
  /** Execute the dice roll for the current turn */
  rollTheDice: () => void;
  /** Submit an answer for the current question */
  submitAnswer: (answerIndex: number) => void;
  /** Advance to the next player's turn */
  nextTurn: () => void;
  /** Transition to board view phase */
  showBoard: () => void;
  /** Reset game for a rematch with the same players */
  rematch: () => void;
  /** Full reset back to setup screen */
  resetToSetup: () => void;
  /** Mark a question ID as used so it won't be selected again */
  markQuestionUsed: (questionId: string) => void;
  /** Process a wrong answer — update player stats (no points, reset streak) */
  applyWrongAnswer: () => void;
  /** Process a correct answer with a dice roll — advance seats, update score, check win */
  applyCorrectAnswerWithDice: (diceValue: number) => void;
  /** Increment questionsWithHelp on the active player's current class progress */
  incrementHelpCount: () => void;
  /** Apply a behavior dock (flat percentage deduction) to a player's current class grade */
  applyBehaviorDock: (playerId: string, dockPercent: number) => void;
  /** Process an answer in Pure Classroom mode — no dice, counts questions, grades on period completion */
  applyPureClassroomAnswer: (isCorrect: boolean) => void;
  /** Restore a previously saved game state (from localStorage) */
  restoreGame: (savedState: GameState) => void;
}

const GameContext = createContext<GameContextValue | null>(null);

// _______________________________________________
//              PROVIDER
// _______________________________________________

interface GameProviderProps {
  children: ReactNode;
}

export function GameProvider({ children }: GameProviderProps) {
  const [state, setState] = useState<GameState>(() =>
    createInitialGameState([], 'family-road-trip'),
  );
  const [pack, setPack] = useState<QuestionPack | null>(null);

  const loadPack = useCallback((newPack: QuestionPack) => {
    setPack(newPack);
  }, []);

  const addPlayer = useCallback(
    (name: string, ageGroup: AgeGroup, tier: Tier, major?: Major) => {
      setState((prev) => {
        const player = createPlayer(
          name, ageGroup, tier, prev.players, prev.scheduleMode,
          undefined, prev.gameMode, major,
        );
        return { ...prev, players: [...prev.players, player] };
      });
    },
    [],
  );

  const removePlayer = useCallback((playerId: string) => {
    setState((prev) => ({
      ...prev,
      players: prev.players.filter((p) => p.id !== playerId),
    }));
  }, []);

  const setGameMode = useCallback((mode: GameMode) => {
    setState((prev) => ({ ...prev, gameMode: mode }));
  }, []);

  const setScheduleMode = useCallback((mode: ScheduleMode) => {
    setState((prev) => ({ ...prev, scheduleMode: mode }));
  }, []);

  /**
   * Start the game. Regenerates all player schedules based on the current
   * game mode and schedule mode so setup-time changes are reflected.
   * Pure Classroom generates question-count schedules instead of seat-based.
   * If 'choose' mode is active and a custom subject order is provided, that order is used.
   */
  const startGame = useCallback((customSubjectOrder?: Category[]) => {
    setState((prev) => {
      const isPureClassroom = prev.gameMode === 'pureClassroom';

      /* Build the schedule that all players will share */
      let schedule: ClassPeriod[];

      if (prev.scheduleMode === 'choose' && customSubjectOrder) {
        /* Build schedule from the user's chosen subject order */
        if (isPureClassroom) {
          /* Pure Classroom: use question counts instead of seat grids */
          schedule = customSubjectOrder.map((subject, index) => ({
            subject,
            classSize: {
              seats: PURE_CLASSROOM_QUESTIONS[subject],
              grid: [1, PURE_CLASSROOM_QUESTIONS[subject]] as [number, number],
            },
            periodNumber: index + 1,
          }));
          schedule.push({
            subject: FINAL_EXAM_LABEL,
            classSize: {
              seats: PURE_CLASSROOM_FINAL_EXAM_QUESTIONS,
              grid: [1, PURE_CLASSROOM_FINAL_EXAM_QUESTIONS] as [number, number],
            },
            periodNumber: FINAL_EXAM_PERIOD,
          });
        } else {
          schedule = customSubjectOrder.map((subject, index) => ({
            subject,
            classSize: SUBJECT_CLASS_SIZE[subject],
            periodNumber: index + 1,
          }));
          schedule.push({
            subject: FINAL_EXAM_LABEL,
            classSize: FINAL_EXAM,
            periodNumber: FINAL_EXAM_PERIOD,
          });
        }
      } else if (isPureClassroom) {
        schedule = generatePureClassroomSchedule(prev.scheduleMode === 'random');
      } else {
        schedule = generateSchedule(prev.scheduleMode === 'random');
      }

      /* Regenerate all player schedules and reset progress */
      const updatedPlayers = prev.players.map((p) => ({
        ...p,
        schedule,
        classProgress: createEmptyClassProgress(TOTAL_PERIODS),
        currentPeriod: 1,
      }));

      return startGameEngine({ ...prev, players: updatedPlayers });
    });
  }, []);

  const rollTheDice = useCallback(() => {
    const diceValue: DiceValue = rollDice();

    setState((prev) => {
      if (!pack) return prev;

      /* Get the current player's subject based on their class schedule */
      const activePlayer = prev.players[prev.currentPlayerIndex];
      const category = getCurrentSubjectForPlayer(activePlayer);

      /* Select a question for this player from the current class subject */
      const question = selectQuestion(
        pack.questions,
        activePlayer.ageGroup,
        activePlayer.tier,
        category,
        prev.usedQuestionIds,
      );

      const newUsedIds = new Set(prev.usedQuestionIds);
      if (question) {
        newUsedIds.add(question.id);
      }

      return {
        ...prev,
        diceValue,
        currentTurn: {
          ...prev.currentTurn,
          diceValue,
          question,
        },
        currentQuestion: question,
        gamePhase: 'question',
        usedQuestionIds: newUsedIds,
      };
    });
  }, [pack]);

  const submitAnswer = useCallback((answerIndex: number) => {
    setState((prev) => {
      if (!prev.currentQuestion) return prev;

      const isCorrect = answerIndex === prev.currentQuestion.correctAnswer;
      const previousPlayers = prev.players;
      const activePlayer = prev.players[prev.currentPlayerIndex];

      /* Process answer through the new turn manager */
      let updatedPlayer: typeof activePlayer;
      if (isCorrect) {
        updatedPlayer = processCorrectAnswer(activePlayer, prev.diceValue ?? 0);
      } else {
        updatedPlayer = processWrongAnswer(activePlayer);
      }

      /* Build updated players array */
      const updatedPlayers = prev.players.map((p, index) =>
        index === prev.currentPlayerIndex ? updatedPlayer : p,
      );

      /* Determine which host reaction to show */
      let reactionEvent: 'correctAnswer' | 'wrongAnswer' | 'takeLead' | 'streak' | 'comeback';

      if (!isCorrect) {
        reactionEvent = 'wrongAnswer';
      } else if (updatedPlayer.currentStreak >= 3) {
        reactionEvent = 'streak';
      } else if (didPlayerTakeLead(updatedPlayer.id, previousPlayers, updatedPlayers)) {
        reactionEvent = 'takeLead';
      } else {
        reactionEvent = 'correctAnswer';
      }

      const hostReaction = getReaction(reactionEvent, updatedPlayer.name);

      /* Build new state with updated players */
      const newState: GameState = {
        ...prev,
        players: updatedPlayers,
        currentTurn: {
          ...prev.currentTurn,
          selectedAnswer: answerIndex,
          wasCorrect: isCorrect,
          pointsEarned: isCorrect ? (prev.diceValue ?? 0) : 0,
        },
        hostReaction,
      };

      /* Check for winner using new win condition logic */
      const winner = checkWinCondition(newState);

      return {
        ...newState,
        gamePhase: winner ? 'results' : 'answer-reveal',
      };
    });
  }, []);

  const showBoard = useCallback(() => {
    setState((prev) => ({ ...prev, gamePhase: 'board-view' }));
  }, []);

  const nextTurn = useCallback(() => {
    setState((prev) => advanceToNextTurn(prev));
  }, []);

  const rematch = useCallback(() => {
    setState((prev) => resetGameForRematch(prev));
  }, []);

  const resetToSetup = useCallback(() => {
    clearSave();
    setState(createInitialGameState([], 'family-road-trip'));
  }, []);

  /** Restore game state from a previously saved session. */
  const restoreGame = useCallback((savedState: GameState) => {
    setState(savedState);
  }, []);

  /**
   * Auto-save: persist game state to localStorage whenever it changes
   * during active gameplay (not during setup). Skip initial mount.
   */
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (state.gamePhase !== 'setup') {
      saveGame(state);
    }
  }, [state]);

  /** Mark a question ID as used so the selector skips it in future turns. */
  const markQuestionUsed = useCallback((questionId: string) => {
    setState((prev) => {
      const newUsedIds = new Set(prev.usedQuestionIds);
      newUsedIds.add(questionId);
      return { ...prev, usedQuestionIds: newUsedIds };
    });
  }, []);

  /** Process a wrong answer — update current player stats (no points, reset streak). */
  const applyWrongAnswer = useCallback(() => {
    setState((prev) => {
      const activePlayer = prev.players[prev.currentPlayerIndex];
      const updatedPlayer = processWrongAnswer(activePlayer);
      const updatedPlayers = prev.players.map((p, i) =>
        i === prev.currentPlayerIndex ? updatedPlayer : p,
      );
      return { ...prev, players: updatedPlayers };
    });
  }, []);

  /** Process a correct answer with dice roll — advance seats, update score, check win. */
  const applyCorrectAnswerWithDice = useCallback((diceValue: number) => {
    setState((prev) => {
      const activePlayer = prev.players[prev.currentPlayerIndex];
      const updatedPlayer = processCorrectAnswer(activePlayer, diceValue);
      const updatedPlayers = prev.players.map((p, i) =>
        i === prev.currentPlayerIndex ? updatedPlayer : p,
      );

      const newState: GameState = { ...prev, players: updatedPlayers };
      const winner = checkWinCondition(newState);

      return {
        ...newState,
        gamePhase: winner ? 'results' : prev.gamePhase,
      };
    });
  }, []);

  /**
   * Increment questionsWithHelp on the active player's current class progress.
   * Called when a player uses Teacher's Desk help and answers correctly.
   * Must be called BEFORE applyCorrectAnswerWithDice so the graduation
   * letter grade calculation includes the knockdown for this question.
   */
  const incrementHelpCount = useCallback(() => {
    setState((prev) => {
      const activePlayer = prev.players[prev.currentPlayerIndex];
      const periodIndex = activePlayer.currentPeriod - 1;
      const currentProgress = activePlayer.classProgress[periodIndex];

      const updatedProgress = {
        ...currentProgress,
        questionsWithHelp: currentProgress.questionsWithHelp + 1,
      };

      const updatedClassProgress = [...activePlayer.classProgress];
      updatedClassProgress[periodIndex] = updatedProgress;

      const updatedPlayer = { ...activePlayer, classProgress: updatedClassProgress };
      const updatedPlayers = prev.players.map((p, i) =>
        i === prev.currentPlayerIndex ? updatedPlayer : p,
      );

      return { ...prev, players: updatedPlayers };
    });
  }, []);

  /**
   * Apply a behavior dock to a specific player's current class progress.
   * Adds the given percentage to the player's cumulative behaviorDock value
   * for the class they are currently attending.
   */
  const applyBehaviorDock = useCallback((playerId: string, dockPercent: number) => {
    setState((prev) => {
      const playerIndex = prev.players.findIndex((p) => p.id === playerId);
      if (playerIndex === -1) return prev;

      const player = prev.players[playerIndex];
      const periodIndex = player.currentPeriod - 1;
      const currentProgress = player.classProgress[periodIndex];

      const updatedProgress = {
        ...currentProgress,
        behaviorDock: currentProgress.behaviorDock + dockPercent,
      };

      const updatedClassProgress = [...player.classProgress];
      updatedClassProgress[periodIndex] = updatedProgress;

      const updatedPlayer = { ...player, classProgress: updatedClassProgress };
      const updatedPlayers = prev.players.map((p, i) =>
        i === playerIndex ? updatedPlayer : p,
      );

      return { ...prev, players: updatedPlayers };
    });
  }, []);

  /**
   * Process an answer in Pure Classroom mode.
   * No dice rolling or seat advancement. Every answer (correct or wrong)
   * counts toward period completion. When all questions in a period are
   * answered, the player is graded and advances to the next period.
   */
  const applyPureClassroomAnswer = useCallback((isCorrect: boolean) => {
    setState((prev) => {
      const activePlayer = prev.players[prev.currentPlayerIndex];
      const updatedPlayer = processPureClassroomAnswer(activePlayer, isCorrect);
      const updatedPlayers = prev.players.map((p, i) =>
        i === prev.currentPlayerIndex ? updatedPlayer : p,
      );

      const newState: GameState = { ...prev, players: updatedPlayers };
      const winner = checkWinCondition(newState);

      return {
        ...newState,
        gamePhase: winner ? 'results' : prev.gamePhase,
      };
    });
  }, []);

  const value = useMemo<GameContextValue>(
    () => ({
      state,
      pack,
      loadPack,
      addPlayer,
      removePlayer,
      setGameMode,
      setScheduleMode,
      startGame,
      rollTheDice,
      submitAnswer,
      nextTurn,
      showBoard,
      rematch,
      resetToSetup,
      markQuestionUsed,
      applyWrongAnswer,
      applyCorrectAnswerWithDice,
      incrementHelpCount,
      applyBehaviorDock,
      applyPureClassroomAnswer,
      restoreGame,
    }),
    [state, pack, loadPack, addPlayer, removePlayer, setGameMode, setScheduleMode, startGame, rollTheDice, submitAnswer, nextTurn, showBoard, rematch, resetToSetup, markQuestionUsed, applyWrongAnswer, applyCorrectAnswerWithDice, incrementHelpCount, applyBehaviorDock, applyPureClassroomAnswer, restoreGame],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

// _______________________________________________
//              HOOK
// _______________________________________________

/** Access the game state and actions from any component. */
export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) {
    throw new Error('useGame must be used within a <GameProvider>');
  }
  return ctx;
}
